import { ICellRendererParams } from "ag-grid-community";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";

import { IgButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  OrganizationSummaryDto,
  StripeSubscriptionSummaryDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useLoggedInUser } from "@src/api/user-auths/login";
import { NoBillingAlert } from "@src/features/admin/components/NoBillingAlert";
import { getHasStripeBillingMethod } from "@src/features/admin/components/utils";
import { LegacySubscriptionsPanel } from "@src/features/admin/pages/overview/components/Expansions/SubscriptionsPanel/LegacySubscriptionsPanel";
import { SubscriptionPanel } from "@src/features/admin/pages/overview/components/Expansions/SubscriptionsPanel/SubscriptionPanel";
import { openDrawer } from "@src/store/slices/drawers";

export const DetailsRenderer = (
  params: ICellRendererParams<OrganizationSummaryDto>
) => {
  const dispatch = useDispatch();
  const user = useLoggedInUser();

  const cannotCreateExtraSubs = !(
    (user.data?.user.email &&
      ["christian@intelligems.io", "samantha@intelligems.io"].includes(
        user.data.user.email
      )) ||
    false
  );

  const { hasBilling, activeSub, apartOfAgency } = useMemo(() => {
    if (!params.data) {
      return { activeSub: false, hasBilling: true };
    }

    const activeSub = params.data?.stripeSubscriptionSummaries.some((s) =>
      ["active", "trialing"].includes(s.status)
    );
    const hasBilling = getHasStripeBillingMethod(
      params.data.stripeSubscriptionSummaries
    );
    const apartOfAgency = !!params.data?.agencies?.length;

    return { activeSub, hasBilling, apartOfAgency };
  }, [params.data]);

  if (!params.data) {
    return null;
  }

  return (
    <Grid
      container
      direction={"column"}
      rowSpacing={2}
      sx={{
        paddingTop: "20px",
        paddingLeft: "20px",
        paddingRight: "20px",
        paddingBottom: "5px",
      }}
    >
      {hasBilling ? null : (
        <NoBillingAlert
          stripeCustomerId={
            params.data.stripeSubscriptionSummaries.find(
              (s) => !!s.customer.id && !s.customer.billing
            )!.customer.id
          }
        />
      )}
      {params.data.organizationSubscriptions.length ? (
        params.data.organizationSubscriptions.every(
          (s) => s.status === "cancelled"
        ) ? (
          <Grid item textAlign={"center"}>
            <IgTypography variant={"body2"}>
              All Legacy Subscriptions Cancelled
            </IgTypography>
          </Grid>
        ) : (
          <Grid item>
            <LegacySubscriptionsPanel
              org={{ id: params.data.id, shopId: params.data.shopId }}
              legacySubscriptions={params.data.organizationSubscriptions}
            />
          </Grid>
        )
      ) : null}
      {params.data.stripeSubscriptionSummaries.length
        ? params.data.stripeSubscriptionSummaries.map(
            (s: StripeSubscriptionSummaryDto, i) => (
              <Grid container item key={`${s.id}-${i}`}>
                <Grid item xs={12}>
                  <SubscriptionPanel subscription={s} adminOrg={params.data!} />
                </Grid>
              </Grid>
            )
          )
        : null}
      <Grid item textAlign={"center"}>
        <IgTooltip
          title={
            activeSub && cannotCreateExtraSubs
              ? "Active Subscription Exists"
              : apartOfAgency
                ? "Part of Agency"
                : ""
          }
        >
          <span>
            <IgButton
              disabled={(activeSub && cannotCreateExtraSubs) || apartOfAgency}
              onClick={() =>
                dispatch(
                  openDrawer({
                    type: "createSubscription",
                    usages: params.data?.usages || null,
                    orgs: [
                      { id: params.data!.id, shopId: params.data!.shopId },
                    ],
                    agencyId: "",
                  })
                )
              }
            >
              Create Subscription
            </IgButton>
          </span>
        </IgTooltip>
      </Grid>
    </Grid>
  );
};
